// frontend/src/pages/mentor/InternDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { 
    ArrowLeftIcon, 
    PlusIcon, 
    CalendarIcon, 
    DocumentTextIcon, 
    LightBulbIcon,
    ClockIcon
} from '@heroicons/react/24/outline';

const InternDetail = () => {
    const { internId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('notes');

    const [noteForm, setNoteForm] = useState({ note: '', category: 'general' });
    const [recForm, setRecForm] = useState({ title: '', description: '', recommendation_type: 'next_step', priority: 'medium' });
    const [sessionForm, setSessionForm] = useState({ scheduled_at: '', duration_minutes: 30, type: 'check_in', agenda: '' });

    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showRecModal, setShowRecModal] = useState(false);
    const [showSessionModal, setShowSessionModal] = useState(false);

    useEffect(() => {
        loadInternDetails();
    }, [internId]);

    const loadInternDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/mentor/interns/${internId}`);
            setData(response.data.data);
        } catch (error) {
            toast.error('Failed to load intern details');
            navigate('/mentor/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/mentor/interns/${internId}/notes`, noteForm);
            toast.success('Note added');
            setShowNoteModal(false);
            setNoteForm({ note: '', category: 'general' });
            loadInternDetails();
        } catch (error) {
            toast.error('Failed to add note');
        }
    };

    const handleAddRec = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/mentor/interns/${internId}/recommendations`, recForm);
            toast.success('Recommendation created');
            setShowRecModal(false);
            setRecForm({ title: '', description: '', recommendation_type: 'next_step', priority: 'medium' });
            loadInternDetails();
        } catch (error) {
            toast.error('Failed to add recommendation');
        }
    };

    const handleScheduleSession = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/mentor/sessions', { ...sessionForm, intern_id: internId });
            toast.success('Session scheduled');
            setShowSessionModal(false);
            setSessionForm({ scheduled_at: '', duration_minutes: 30, type: 'check_in', agenda: '' });
            loadInternDetails();
        } catch (error) {
            toast.error('Failed to schedule session');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="skeleton h-40" />
                <span className="sr-only">Loading intern details…</span>
            </div>
        );
    }

    if (!data) return null;
    const { user, profile, notes, recommendations, sessions, hours, stats } = data;

    return (
        <div className="w-full space-y-6">
            <Link to="/mentor/dashboard" className="inline-flex items-center text-sm font-medium text-ink-600 hover:text-brand-800 gap-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Mentor Dashboard</span>
            </Link>

            <div className="card space-y-4 animate-fade-up">
                <div className="flex justify-between items-start flex-wrap gap-4">
                    <div>
                        <h1 className="page-title">
                            {user?.first_name} {user?.last_name}
                        </h1>
                        <p className="text-ink-600 text-sm mt-0.5">{user?.email} • {user?.phone || 'No phone'}</p>
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setShowSessionModal(true)}
                            className="btn-primary !min-h-[36px] !py-1.5 text-sm"
                        >
                            <CalendarIcon className="w-4 h-4" aria-hidden="true" />
                            Schedule Session
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-border pt-4">
                    <div>
                        <span className="text-ink-500 block text-xs">Approved Hours</span>
                        <span className="font-semibold text-green-600">{stats?.totalHours || 0} hrs</span>
                    </div>
                    <div>
                        <span className="text-ink-500 block text-xs">Pending Hours</span>
                        <span className="font-semibold text-yellow-600">{stats?.pendingHours || 0} hrs</span>
                    </div>
                    <div>
                        <span className="text-ink-500 block text-xs">Assessments</span>
                        <span className="font-semibold">{stats?.assessments || 0}</span>
                    </div>
                    <div>
                        <span className="text-ink-500 block text-xs">Projects</span>
                        <span className="font-semibold">{stats?.projects || 0}</span>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 border-b border-border">
                {['notes', 'recommendations', 'sessions'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-2 px-1 text-sm font-medium capitalize border-b-2 transition-colors ${
                            activeTab === tab 
                                ? 'border-brand-700 text-brand-800' 
                                : 'border-transparent text-ink-500 hover:text-ink-700'
                        }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Tab Contents */}
            {activeTab === 'notes' && (
                <div className="card p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-ink-950">Private Notes</h3>
                        <button
                            onClick={() => setShowNoteModal(true)}
                            className="px-3 py-1.5 bg-brand-700 text-white text-xs font-medium rounded hover:bg-brand-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Note</span>
                        </button>
                    </div>

                    {notes?.length === 0 ? (
                        <p className="text-sm text-ink-500 py-2">No notes recorded yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {notes?.map((n) => (
                                <div key={n.id} className="rounded-xl border border-border p-3 text-sm">
                                    <div className="flex justify-between items-center text-xs text-ink-500 mb-1">
                                        <span className="font-medium capitalize px-2 py-0.5 bg-ink-100 rounded text-ink-700">{n.category}</span>
                                        <span>{new Date(n.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <p className="text-ink-800 mt-1">{n.note}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'recommendations' && (
                <div className="card p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-ink-950">Recommendations</h3>
                        <button
                            onClick={() => setShowRecModal(true)}
                            className="px-3 py-1.5 bg-brand-700 text-white text-xs font-medium rounded hover:bg-brand-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Recommendation</span>
                        </button>
                    </div>

                    {recommendations?.length === 0 ? (
                        <p className="text-sm text-ink-500 py-2">No recommendations created yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {recommendations?.map((r) => (
                                <div key={r.id} className="rounded-xl border border-border p-4">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-semibold text-sm text-ink-950">{r.title}</h4>
                                            <p className="text-xs text-ink-600 mt-1">{r.description}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium capitalize">
                                            {r.priority}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'sessions' && (
                <div className="card p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-ink-950">Session History</h3>
                        <button
                            onClick={() => setShowSessionModal(true)}
                            className="px-3 py-1.5 bg-brand-700 text-white text-xs font-medium rounded hover:bg-brand-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Schedule Session</span>
                        </button>
                    </div>

                    {sessions?.length === 0 ? (
                        <p className="text-sm text-ink-500 py-2">No sessions scheduled.</p>
                    ) : (
                        <div className="space-y-3">
                            {sessions?.map((s) => (
                                <div key={s.id} className="rounded-xl border border-border p-3 flex justify-between items-center text-sm">
                                    <div>
                                        <p className="font-medium text-ink-950">{new Date(s.scheduled_at).toLocaleString()}</p>
                                        <p className="text-xs text-ink-500 capitalize">{s.type} • {s.duration_minutes} min</p>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded bg-ink-100 font-medium capitalize">
                                        {s.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Note Modal */}
            {showNoteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddNote} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-ink-950">Add Private Note</h3>
                        <select
                            value={noteForm.category}
                            onChange={(e) => setNoteForm({ ...noteForm, category: e.target.value })}
                            className="input-field"
                        >
                            <option value="general">General</option>
                            <option value="performance">Performance</option>
                            <option value="behavior">Behavior</option>
                            <option value="skills">Skills</option>
                            <option value="recommendation">Recommendation</option>
                        </select>
                        <textarea
                            required
                            placeholder="Write private note..."
                            value={noteForm.note}
                            onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })}
                            className="input-field"
                            rows={3}
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowNoteModal(false)} className="px-3 py-1.5 text-xs text-ink-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-brand-700 text-white text-xs rounded hover:bg-brand-800">Save Note</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Recommendation Modal */}
            {showRecModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddRec} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-ink-950">Add Recommendation</h3>
                        <input
                            type="text"
                            required
                            placeholder="Title"
                            value={recForm.title}
                            onChange={(e) => setRecForm({ ...recForm, title: e.target.value })}
                            className="input-field"
                        />
                        <textarea
                            placeholder="Description"
                            value={recForm.description}
                            onChange={(e) => setRecForm({ ...recForm, description: e.target.value })}
                            className="input-field"
                            rows={2}
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                value={recForm.recommendation_type}
                                onChange={(e) => setRecForm({ ...recForm, recommendation_type: e.target.value })}
                                className="input-field"
                            >
                                <option value="next_step">Next Step</option>
                                <option value="training">Training</option>
                                <option value="business">Business</option>
                                <option value="project">Project</option>
                                <option value="certification">Certification</option>
                            </select>
                            <select
                                value={recForm.priority}
                                onChange={(e) => setRecForm({ ...recForm, priority: e.target.value })}
                                className="input-field"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowRecModal(false)} className="px-3 py-1.5 text-xs text-ink-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-brand-700 text-white text-xs rounded hover:bg-brand-800">Submit</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Session Modal */}
            {showSessionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleScheduleSession} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-ink-950">Schedule 1-on-1 Session</h3>
                        <input
                            type="datetime-local"
                            required
                            value={sessionForm.scheduled_at}
                            onChange={(e) => setSessionForm({ ...sessionForm, scheduled_at: e.target.value })}
                            className="input-field"
                        />
                        <select
                            value={sessionForm.type}
                            onChange={(e) => setSessionForm({ ...sessionForm, type: e.target.value })}
                            className="input-field"
                        >
                            <option value="check_in">Check In</option>
                            <option value="review">Review</option>
                            <option value="assessment">Assessment</option>
                            <option value="project">Project</option>
                            <option value="career">Career</option>
                        </select>
                        <textarea
                            placeholder="Agenda / Notes"
                            value={sessionForm.agenda}
                            onChange={(e) => setSessionForm({ ...sessionForm, agenda: e.target.value })}
                            className="input-field"
                            rows={2}
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowSessionModal(false)} className="px-3 py-1.5 text-xs text-ink-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-brand-700 text-white text-xs rounded hover:bg-brand-800">Schedule</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default InternDetail;
